import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Sparkles, Loader2, Plus, Check } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import SmartImage from "@/components/common/SmartImage";

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const initial = (params.get("slugs") || "").split(",").filter(Boolean);
  const [allCars, setAllCars] = useState([]);
  const [selected, setSelected] = useState(initial);
  const [query, setQuery] = useState("");
  const [showPicker, setShowPicker] = useState(initial.length < 2);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState("");

  useEffect(() => {
    api.get("/cars?limit=200").then((r) => setAllCars(r.data));
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return allCars.filter((c) => !selected.includes(c.slug) && (c.model.toLowerCase().includes(q) || c.brand_name.toLowerCase().includes(q))).slice(0, 12);
  }, [allCars, query, selected]);

  const selectedCars = selected.map((s) => allCars.find((c) => c.slug === s)).filter(Boolean);

  const addCar = (slug) => {
    if (selected.length >= 4) return;
    const next = [...selected, slug];
    setSelected(next);
    setParams({ slugs: next.join(",") }, { replace: true });
    if (next.length >= 2) setShowPicker(false);
  };

  const removeCar = (slug) => {
    const next = selected.filter((s) => s !== slug);
    setSelected(next);
    setParams({ slugs: next.join(",") }, { replace: true });
  };

  const compare = async () => {
    if (selected.length < 2) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/compare", { car_slugs: selected, user_context: context || null });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "AI compare failed" });
    } finally { setLoading(false); }
  };

  const specs = ["price_min", "body_type", "fuel_types", "engine", "power_bhp", "torque_nm", "mileage_kmpl", "range_km", "seats", "airbags", "ncap_rating", "sunroof", "adas"];
  const labelFor = (k) => ({
    price_min: "Starting price", body_type: "Body", fuel_types: "Fuel options", engine: "Engine", power_bhp: "Power (bhp)",
    torque_nm: "Torque (Nm)", mileage_kmpl: "Mileage (kmpl)", range_km: "Range (km)", seats: "Seats",
    airbags: "Airbags", ncap_rating: "NCAP", sunroof: "Sunroof", adas: "ADAS",
  }[k] || k);
  const valueFor = (car, k) => {
    if (k === "price_min") return formatINR(car.price_min);
    if (k === "fuel_types") return (car.fuel_types || []).join(", ");
    if (k === "body_type") return car.body_type;
    const v = car.specs?.[k];
    if (v === true) return "Yes";
    if (v === false || v == null) return "—";
    if (k === "ncap_rating") return v ? `${v}★` : "—";
    return v;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="compare-page">
      <SEO title="AI Car Comparison" description="Compare cars side-by-side with an AI verdict that names a clear winner."/>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI Compare</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">Side-by-side, with a verdict.</h1>
      <p className="text-muted-foreground mt-3 max-w-xl">Pick 2–4 cars. We'll lay them out and our AI will name a winner with reasons.</p>

      <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="selected-row">
        {selectedCars.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-border bg-card/30 p-2 pr-3 flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0"><SmartImage src={c.image} alt={c.model} compact/></div>
            <span className="text-sm">{c.brand_name} {c.model}</span>
            <button data-testid={`remove-${c.slug}`} onClick={() => removeCar(c.slug)} className="ml-2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
          </div>
        ))}
        {selected.length < 4 && (
          <button data-testid="open-picker" onClick={() => setShowPicker(true)} className="h-12 px-4 rounded-2xl border border-dashed border-border text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Add a car</button>
        )}
      </div>

      {showPicker && (
        <div className="mt-6 rounded-3xl border border-border bg-card/30 p-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
            <input data-testid="picker-search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Search cars by brand or model…" className="w-full h-11 pl-9 pr-3 rounded-xl bg-secondary/50 border border-border outline-none focus:border-foreground/40"/>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-auto">
            {filtered.map((c) => (
              <button key={c.slug} data-testid={`pick-${c.slug}`} onClick={() => addCar(c.slug)} className="flex items-center gap-3 p-2 rounded-xl border border-border hover:bg-secondary text-left">
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0"><SmartImage src={c.image} alt={c.model} compact/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{c.brand_name}</div>
                  <div className="text-sm truncate">{c.model}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input data-testid="compare-context" value={context} onChange={(e)=>setContext(e.target.value)} placeholder="Context (optional): e.g. young family in Bengaluru…" className="h-11 px-4 rounded-full bg-secondary/50 border border-border w-full sm:w-96 outline-none"/>
        <button data-testid="compare-run" disabled={selected.length < 2 || loading} onClick={compare} className="h-11 px-6 rounded-full bg-gradient-ai text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Thinking…</> : <><Sparkles className="w-4 h-4"/> Get AI verdict</>}
        </button>
      </div>

      {selectedCars.length >= 2 && (
        <div className="mt-10 rounded-3xl border border-border overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-4 text-muted-foreground font-normal text-xs uppercase tracking-[0.2em] w-44">Spec</th>
                {selectedCars.map((c) => (
                  <th key={c.slug} className="text-left p-4 min-w-[180px]">
                    <div className="flex items-center gap-2"><div className="w-10 h-10 rounded-lg overflow-hidden shrink-0"><SmartImage src={c.image} alt="" compact/></div><span className="font-medium">{c.brand_name} {c.model}</span></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {specs.map((k) => (
                <tr key={k} className="border-t border-border">
                  <td className="p-4 text-muted-foreground">{labelFor(k)}</td>
                  {selectedCars.map((c) => <td key={c.slug} className="p-4">{valueFor(c, k)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && !result.error && (
        <div className="mt-8 grid lg:grid-cols-3 gap-5" data-testid="compare-result">
          <div className="lg:col-span-2 rounded-3xl border border-border bg-card/30 p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-ai font-semibold">AI Verdict</div>
            <div className="font-display text-2xl mt-2 tracking-tight leading-snug">{result.verdict}</div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {Object.entries(result.category_winners || {}).map(([cat, slug]) => {
                const car = selectedCars.find((c) => c.slug === slug);
                return (
                  <div key={cat} className="rounded-2xl border border-border p-3 flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">{cat}</span>
                    <span className="text-sm font-medium inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500"/>{car ? `${car.brand_name} ${car.model}` : slug}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-3xl border border-border bg-card/30 p-6 space-y-5">
            {selectedCars.map((c) => {
              const pc = result.pros_cons?.[c.slug];
              if (!pc) return null;
              return (
                <div key={c.slug}>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{c.brand_name} {c.model}</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {pc.pros?.map((p, i) => <li key={`p${i}`} className="flex gap-2"><span className="text-emerald-500">+</span>{p}</li>)}
                    {pc.cons?.map((p, i) => <li key={`c${i}`} className="flex gap-2 text-muted-foreground"><span className="text-destructive">–</span>{p}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {result?.error && <div className="mt-4 text-destructive text-sm" data-testid="compare-error">{result.error}</div>}
    </div>
  );
}
